import { createTheme } from "@mui/material/styles";
import hexToRgba from "utils/hexToRgba";
import { ensureContrast } from "theme/contrast";
import { FONT_FAMILY } from "theme/fonts";

const TEXT_PRIMARY = "#1f2933";
const TEXT_SECONDARY = "#52606d";
const BACKGROUND = "#f6f7f9";
const SHADOW = "rgba(16, 24, 40";
// Soft shadows for elevations 1-8 (cards, menus, popovers); higher ones keep
// MUI's defaults (rarely used: dialogs, drawers).
const SOFT_SHADOWS = [
  "none",
  `0 1px 2px ${SHADOW}, 0.06), 0 1px 3px ${SHADOW}, 0.10)`,
  `0 1px 3px ${SHADOW}, 0.08), 0 2px 6px ${SHADOW}, 0.08)`,
  `0 2px 4px ${SHADOW}, 0.06), 0 4px 8px ${SHADOW}, 0.08)`,
  `0 4px 8px ${SHADOW}, 0.06), 0 8px 16px ${SHADOW}, 0.08)`,
];
// Focus ring for items whose container clips outside them (tabs, menus,
// lists): drawn inside the item, with the white halo under its inner edge.
const INSIDE_RING = { outlineOffset: -3, boxShadow: "inset 0 0 0 5px #ffffff" };
const shadows = (defaults) =>
  defaults.map((s, i) => SOFT_SHADOWS[i] ?? (i <= 8 ? SOFT_SHADOWS[4] : s));

/**
 * Creates a Material-UI theme based on primary and secondary colors
 *
 * @param {string} rawMainColor - The primary color in hex format (e.g., '#6699cc')
 * @param {string} rawSecondaryColor - The secondary color in hex format (e.g., '#203b0e')
 * @returns {Theme} Material-UI theme object
 *
 * Features:
 * - Custom color palette with opaque variants; pale site colours are
 *   darkened to WCAG AA on white (normalised to lowercase #rrggbb)
 * - Light surfaces, Noto Sans, larger body text, rounded corners, soft
 *   shadows, flat sentence-case buttons and a keyboard focus ring
 * - Clear, high-contrast tooltips with an arrow
 * - 44px icon buttons on every device (except dense and in-input ones)
 * - Custom input base styling with disabled state
 */
export function createAppTheme(rawMainColor, rawSecondaryColor) {
  // Pale site colours are darkened just enough to read on white (WCAG AA).
  const mainColor = ensureContrast(rawMainColor);
  const secondaryColor = ensureContrast(rawSecondaryColor);
  // Create opaque variants of the colors for transparency effects
  const mainOpaqueColor = hexToRgba(mainColor, 0.5);
  const secondaryOpaqueColor = hexToRgba(secondaryColor, 0.5);

  const base = createTheme();
  return createTheme({
    spacing: 8,
    shape: { borderRadius: 8 },
    shadows: shadows(base.shadows),
    palette: {
      // Text on primary/secondary buttons switches to dark below AA.
      contrastThreshold: 4.5,
      primary: {
        main: mainColor,
        mainOpaque: mainOpaqueColor,
      },
      secondary: {
        main: secondaryColor,
        mainOpaque: secondaryOpaqueColor,
      },
      // MUI's info, success and warning mains are below AA as text on white
      // and on the page background (chips, owned-layer rows, warning icons
      // and hints); light and dark keep MUI's values. MUI's warning.dark
      // (#e65100) is lighter than this main, so use main for warning text.
      info: { main: "#0271b3", light: "#03a9f4", dark: "#01579b" },
      success: { main: "#256b29", light: "#4caf50", dark: "#1b5e20" },
      warning: { main: "#b25000", light: "#ff9800", dark: "#e65100" },
      background: { default: BACKGROUND, paper: "#ffffff" },
      text: { primary: TEXT_PRIMARY, secondary: TEXT_SECONDARY },
      divider: "#e4e7eb",
    },
    typography: {
      fontFamily: FONT_FAMILY,
      body1: { fontSize: "1rem" },
      body2: { fontSize: "0.9375rem" },
      button: { fontSize: "0.9375rem" },
      caption: { fontSize: "0.8125rem" },
    },
    components: {
      // Keyboard focus ring on every MUI control (buttons, list items, tabs,
      // switches...); mouse clicks do not show it. Two-tone so it shows on
      // any background: the site colour ring, plus a white halo filling the
      // gap between control and ring, which is what stays visible on a
      // primary-coloured AppBar or contained button.
      MuiButtonBase: {
        styleOverrides: {
          root: {
            "&.Mui-focusVisible": {
              outline: `3px solid ${mainColor}`,
              outlineOffset: 2,
              boxShadow: "0 0 0 2px #ffffff",
              // Tabs, menus and lists clip anything drawn outside their
              // items (overflow hidden/auto), so draw the ring, and the
              // halo under its inner edge, inside the item.
              "&.MuiTab-root": INSIDE_RING,
              "&.MuiMenuItem-root": INSIDE_RING,
              "&.MuiListItemButton-root": INSIDE_RING,
            },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 8,
            // Small buttons sit in dense rows (tables, legend) and keep
            // their size.
            "&:not(.MuiButton-sizeSmall)": { minHeight: 44 },
          },
        },
      },
      MuiCard: { styleOverrides: { root: { borderRadius: 12 } } },
      // Clear tooltips: an arrow to what they describe, larger text, white
      // on near-black (about 14:1) whatever the site colours, and a short
      // delay so they do not flash up while the pointer passes by.
      MuiTooltip: {
        defaultProps: { arrow: true, enterDelay: 300, enterNextDelay: 300 },
        styleOverrides: {
          tooltip: {
            fontSize: "0.875rem",
            lineHeight: 1.4,
            padding: "6px 10px",
            maxWidth: 280,
            borderRadius: 6,
            color: "#ffffff",
            backgroundColor: TEXT_PRIMARY,
          },
          arrow: { color: TEXT_PRIMARY },
        },
      },
      // Custom input base styling
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: "white",
            // Search fields with an end adornment draw their own clear
            // button; hide the browser's, which showed as a second "x".
            "&.MuiInputBase-adornedEnd input::-webkit-search-cancel-button": {
              display: "none",
            },
            "&.Mui-disabled": {
              backgroundColor: "#f5f5f5",
            },
          },
        },
      },
      // Apple/Google minimum touch target, on every device: easier to hit
      // for people who are not used to a mouse.
      MuiIconButton: {
        styleOverrides: {
          root: {
            minWidth: 44,
            minHeight: 44,
            // A text field's adornment (e.g. the password visibility
            // toggle) is clamped to the input height; keep it at its size.
            ".MuiInputAdornment-root &": { minWidth: 0, minHeight: 0 },
            // Autocomplete clear/popup indicators sit inside the input too.
            ".MuiAutocomplete-endAdornment &": { minWidth: 0, minHeight: 0 },
            // size="small" marks dense rows (legend, tables); 44px would
            // blow up their layout, so they keep their compact size.
            "&.MuiIconButton-sizeSmall": { minWidth: 0, minHeight: 0 },
          },
        },
      },
      // Phones: dialogs never extend past the dynamic viewport (address bar,
      // keyboard), and scroll inside instead. Full-screen dialogs stay edge
      // to edge.
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({
            borderRadius: 12,
            // MUI squares full-screen dialogs itself, but this override comes
            // after its styles; keep them square.
            "&.MuiDialog-paperFullScreen": { borderRadius: 0 },
            [theme.breakpoints.down("md")]: {
              margin: 16,
              maxHeight: "calc(var(--app-vh) - 32px)",
              "&.MuiDialog-paperFullScreen": { margin: 0, maxHeight: "none" },
            },
          }),
        },
      },
    },
  });
}
